import React, { useState, useEffect, useRef } from 'react';
import { Search, Bell, MessageSquare } from 'react-feather';
import { Download, Printer, CalendarClock, RefreshCw, Share2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getUnreadNotificationCount } from '../../../utils/api';
import { useUserSettingsContext } from '../../../context/UserSettingsContext';
import { useStreamChat } from '../../../context/StreamChatContext';
import { useNavbarActionSlot, useNavbarTrayHidden } from '../../../context/NavbarActionsContext';
import NotificationCenter from '../../NotificationCenter/NotificationCenter';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Separator } from '../../ui/separator';
import { cn } from '../../../lib/utils';
import { NATIVE_REPORTS_CATALOG, filterCatalogByReportGroups } from '../../../config/nativeReportsCatalog';
import { useReportCatalogFlags } from '../../../hooks/useReportCatalogFlags';

// Icons for page-registered tray actions (see NavbarActionsContext) — actions
// cross the context as plain data, the tray owns the visuals.
const ACTION_ICONS = {
  excel: Download,
  pdf: Printer,
  schedule: CalendarClock,
  share: Share2,
  refresh: RefreshCw,
};

/** Animated container: slides page actions out of the tray on mount. */
function TrayActions({ children }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return (
    <div className={cn(
      'flex items-center gap-1 overflow-hidden transition-all duration-300 motion-reduce:transition-none',
      open ? 'max-w-[300px] opacity-100' : 'max-w-0 opacity-0'
    )}>
      {children}
    </div>
  );
}

const Navbar = ({ isSidebarCollapsed, toggleChat, sidebarHovered }) => {
  const [showNotificationCenter, setShowNotificationCenter] = useState(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const notificationRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    userSettings,
    currentOrganization
  } = useUserSettingsContext();
  const catalogFlags = useReportCatalogFlags();
  const { chatClient, unreadMessageCount } = useStreamChat();
  const pageActions = useNavbarActionSlot();
  const trayHidden = useNavbarTrayHidden();

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  // Floating tray: search lives collapsed behind its icon and expands
  // leftward inside the tray on click or "/" — the full-width bar is gone.
  const [searchExpanded, setSearchExpanded] = useState(false);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);

  const loadUnreadCount = async () => {
    if (userSettings?.email) {
      try {
        const count = await getUnreadNotificationCount(userSettings.email);
        setUnreadNotificationCount(count);
      } catch (error) {
        console.error('Error loading unread notification count:', error);
      }
    }
  };

  useEffect(() => {
    loadUnreadCount();
  }, [userSettings?.email]);

  useEffect(() => {
    if (!userSettings?.email) return;
    
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadUnreadCount();
      }
    }, 60000);
    
    return () => clearInterval(interval);
  }, [userSettings?.email]);

  useEffect(() => {
    if (!userSettings?.email) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setTimeout(loadUnreadCount, 1000);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [userSettings?.email]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotificationCenter(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
        setSearchExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setSearchTerm('');
    setSearchResults([]);
    setShowResults(false);
    setSearchExpanded(false);
  }, [location.pathname]);

  // Focus the input once the tray has expanded.
  useEffect(() => {
    if (searchExpanded) searchInputRef.current?.focus();
  }, [searchExpanded]);

  // "/" opens search from anywhere (except while typing elsewhere).
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      const el = event.target;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      event.preventDefault();
      setSearchExpanded(true);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSearch = (value) => {
    setSearchTerm(value);
    const needle = value.trim().toLowerCase();
    if (!needle) {
      setSearchResults([]);
      return;
    }

    // Report-group grants (mapped server-side to orgAccess.nativeReports) gate
    // search the same way they gate the sidebar. null = unrestricted.
    const nativeGroups = userSettings?.orgAccess?.[currentOrganization?.code]?.nativeReports;
    const catalog = filterCatalogByReportGroups(NATIVE_REPORTS_CATALOG, nativeGroups, catalogFlags);
    const results = catalog.flatMap((g) =>
      g.pages
        .filter((p) => p.title.toLowerCase().includes(needle) || g.group.toLowerCase().includes(needle))
        .map((p) => ({ id: p.key, type: 'page', name: p.title, reportName: g.group, path: p.path }))
    );

    setSearchResults(results);
    setShowResults(true);
  };

  const handleResultClick = (item) => {
    navigate(item.path);
    setSearchTerm('');
    setShowResults(false);
  };


  return (
    <div className={cn(
      // z-40: above page-level sticky headers (e.g. Journal Export's frosted
      // z-30 header, which otherwise blurs the tray into a blank white pill)
      'fixed top-4 right-4 h-12 flex items-center gap-1 px-1.5 z-40',
      'border border-slate-200',
      'dark:bg-slate-900 dark:border-slate-700/50',
      'shadow-xl rounded-full',
      // A page panel that claims this corner (Forecast V3's docked AI chat)
      // slides the tray out via useHideTray — its own controls sit here.
      'transition-transform duration-300 motion-reduce:transition-none',
      trayHidden && 'translate-x-[calc(100%+1.5rem)] pointer-events-none'
    )}
    aria-hidden={trayHidden || undefined}
    style={{ background: 'var(--lodge-white)' }}>

      {/* Page actions (report Excel/PDF/Schedule/Refresh) — animate in when a
          page registers them, leftmost so the tray grows away from the edge */}
      {pageActions.length > 0 && (
        <>
          <TrayActions>
            {pageActions.map((action) => {
              const Icon = ACTION_ICONS[action.key];
              return (
                <Button
                  key={action.key}
                  variant="ghost"
                  size="icon"
                  aria-label={action.label}
                  title={action.label}
                  onClick={action.onClick}
                  disabled={action.disabled}
                  className={cn(
                    'h-9 w-9 rounded-full shrink-0',
                    action.primary && 'bg-slate-900 text-white hover:bg-slate-700 hover:text-white dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-300'
                  )}
                >
                  {Icon && (
                    <Icon
                      size={18}
                      className={cn(
                        action.primary ? 'text-current' : 'text-slate-600 dark:text-slate-400',
                        action.spinning && 'animate-spin'
                      )}
                    />
                  )}
                </Button>
              );
            })}
          </TrayActions>
          <Separator orientation="vertical" className="h-6" />
        </>
      )}

      {/* Search: collapsed behind its icon, expands leftward inside the tray */}
      <div className="relative flex items-center" ref={searchRef}>
        <div className={cn(
          'overflow-hidden transition-all duration-300 motion-reduce:transition-none',
          searchExpanded ? 'w-64 sm:w-80 opacity-100 mr-1' : 'w-0 opacity-0'
        )}>
          <Input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            onFocus={() => setShowResults(true)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setShowResults(false);
                setSearchExpanded(false);
              }
            }}
            placeholder="Search reports and pages..."
            className="h-9 px-3 text-sm border-0 shadow-none focus-visible:ring-0 bg-transparent"
          />
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Search reports and pages"
          aria-expanded={searchExpanded}
          onClick={() => setSearchExpanded((open) => !open)}
          className="h-9 w-9 rounded-full"
        >
          <Search size={18} className="text-slate-600 dark:text-slate-400" />
        </Button>

        {/* Search Results Dropdown — anchored under the tray, right-aligned */}
        {searchExpanded && showResults && searchResults.length > 0 && (
          <div className="absolute top-full right-0 mt-3 w-80 sm:w-96 dark:bg-slate-800/95 rounded-xl shadow-xl border dark:border-slate-700/50 max-h-96 overflow-y-auto z-50" style={{ background: 'var(--lodge-white)', borderColor: 'var(--lodge-onyx-100)' }}>
            {searchResults.map((item) => (
              <button
                key={item.id}
                onClick={() => handleResultClick(item)}
                className="w-full px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors border-b border-slate-100 dark:border-slate-800 last:border-0"
              >
                <div className="flex items-center gap-3">
                  <Search size={16} className="text-slate-500 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                      {item.name}
                    </div>
                    {item.type === 'page' && (
                      <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                        in {item.reportName}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <Separator orientation="vertical" className="h-6" />

      {/* Notification Center */}
      <div className="relative z-40" ref={notificationRef}>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Notifications"
          onClick={() => {
            setShowNotificationCenter(!showNotificationCenter);
            if (!showNotificationCenter) {
              loadUnreadCount();
            }
          }}
          className="relative h-9 w-9 rounded-full"
        >
          <Bell size={18} className="text-slate-600 dark:text-slate-400" />
          {unreadNotificationCount > 0 && (
            <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-white text-[11px] font-bold rounded-full shadow-sm" style={{ background: 'var(--lodge-orange-400)' }}>
              {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
            </span>
          )}
        </Button>

        <NotificationCenter
          isOpen={showNotificationCenter}
          onClose={() => setShowNotificationCenter(false)}
          onNavigate={(path) => {
            navigate(path, { state: { forecastJump: Date.now() } });
            setShowNotificationCenter(false);
          }}
          onNotificationRead={loadUnreadCount}
        />
      </div>

      {/* Chat */}
      <Button
        variant="ghost"
        size="icon"
        aria-label="Chat"
        onClick={toggleChat}
        className="relative h-9 w-9 rounded-full"
      >
        <MessageSquare size={18} className="text-slate-600 dark:text-slate-400" />
        {unreadMessageCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-white text-[11px] font-bold rounded-full shadow-sm" style={{ background: 'var(--lodge-orange-400)' }}>
            {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
          </span>
        )}
      </Button>
    </div>
  );
};

export default Navbar;
